import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Instagram, Send, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    guestCount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setIsSuccess(true);
      toast.success('Message sent successfully!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventDate: '',
        guestCount: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Service Area",
      details: ["Atlanta + surrounding region", "Travel within 45 miles included"]
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Phone",
      details: ["(404) 555-ROYAL", "Available 9AM - 8PM"]
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      details: ["info@royaldelights.com", "We respond within 24 hours"]
    },
    {
      icon: <Instagram className="w-6 h-6" />,
      title: "Instagram",
      details: ["@_royaldelights", "Follow for updates & inspiration"]
    }
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p variants={fadeInUp} className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4 font-body">
              Contact
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F0] mb-6">
              Get in Touch
            </motion.h1>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#A1A1AA]">
              We'd love to help you create a soulful luxury experience. Reach out and let's start planning.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 lg:py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="font-heading text-3xl font-bold text-[#F5F5F0] mb-8">
                Contact Information
              </motion.h2>

              <div className="space-y-8">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-12 h-12 bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37]">{info.icon}</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-[#F5F5F0] mb-1">{info.title}</h3>
                      {info.details.map((detail, i) => (
                        <p key={i} className="font-body text-[#A1A1AA]">{detail}</p>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Social Link */}
              <motion.div variants={fadeInUp} className="mt-12">
                <a 
                  href="https://instagram.com/_royaldelights" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F5F5F0] transition-colors font-body"
                >
                  <Instagram className="w-5 h-5" />
                  Follow @_royaldelights on Instagram
                </a>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#121212] border border-[#27272A] p-12 text-center"
                >
                  <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Send className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-[#F5F5F0] mb-4">Message Sent!</h3>
                  <p className="font-body text-[#A1A1AA] mb-6">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <Button
                    onClick={() => setIsSuccess(false)}
                    className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-6 py-3 transition-all duration-300 hover:scale-105"
                  >
                    Send Another Message
                  </Button>
                </motion.div>
              ) : (
                <motion.form
                  onSubmit={handleSubmit}
                  className="bg-[#121212] border border-[#27272A] p-8 sm:p-12"
                  variants={fadeInUp}
                >
                  <h2 className="font-heading text-2xl font-bold text-[#F5F5F0] mb-8">Send Us a Message</h2>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-[#F5F5F0] mb-2 block">Name *</Label>
                        <Input
                          name="name"
                          data-testid="contact-name-input"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="bg-transparent border-[#27272A] text-[#F5F5F0] placeholder:text-[#A1A1AA]"
                        />
                      </div>
                      <div>
                        <Label className="text-[#F5F5F0] mb-2 block">Email *</Label>
                        <Input
                          type="email"
                          name="email"
                          data-testid="contact-email-input"
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="bg-transparent border-[#27272A] text-[#F5F5F0] placeholder:text-[#A1A1AA]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-[#F5F5F0] mb-2 block">Phone</Label>
                        <Input
                          type="tel"
                          name="phone"
                          data-testid="contact-phone-input"
                          placeholder="(123) 456-7890"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="bg-transparent border-[#27272A] text-[#F5F5F0] placeholder:text-[#A1A1AA]"
                        />
                      </div>
                      <div>
                        <Label className="text-[#F5F5F0] mb-2 block">Event Date</Label>
                        <Input
                          type="date"
                          name="eventDate"
                          data-testid="contact-date-input"
                          value={formData.eventDate}
                          onChange={handleInputChange}
                          className="bg-transparent border-[#27272A] text-[#F5F5F0]"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-[#F5F5F0] mb-2 block">Guest Count</Label>
                      <Input
                        type="number"
                        name="guestCount"
                        data-testid="contact-guests-input"
                        placeholder="Estimated number of guests"
                        value={formData.guestCount}
                        onChange={handleInputChange}
                        className="bg-transparent border-[#27272A] text-[#F5F5F0] placeholder:text-[#A1A1AA]"
                      />
                    </div>

                    <div>
                      <Label className="text-[#F5F5F0] mb-2 block">Message *</Label>
                      <Textarea
                        name="message"
                        data-testid="contact-message-input"
                        placeholder="Tell us about your event..."
                        value={formData.message}
                        onChange={handleInputChange}
                        className="bg-transparent border-[#27272A] text-[#F5F5F0] placeholder:text-[#A1A1AA] min-h-[150px]"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                      className="w-full bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-8 py-4 transition-all duration-300 disabled:opacity-50 hover:scale-105"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                </motion.form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map/Service Area Section */}
      <section className="py-24 lg:py-32 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-[#D4AF37]" />
              <p className="text-[#D4AF37] uppercase tracking-widest text-sm font-body">Availability</p>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl font-bold text-[#F5F5F0] mb-6">
              Atlanta & Surrounding Areas
            </motion.h2>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#A1A1AA] max-w-2xl mx-auto mb-8">
              We proudly serve Atlanta and the surrounding region. Travel within 45 miles is included in all bookings. 
              For events beyond this range, we charge $1.25 per mile.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="bg-[#050505] border border-[#27272A] p-8 max-w-md mx-auto"
            >
              <h3 className="font-heading text-xl font-semibold text-[#D4AF37] mb-4">Business Hours</h3>
              <div className="space-y-2 font-body text-[#A1A1AA]">
                <p>Monday - Friday: 9:00 AM - 8:00 PM</p>
                <p>Saturday - Sunday: 10:00 AM - 6:00 PM</p>
                <p className="text-sm mt-4 text-[#F5F5F0]">Events available 7 days a week</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
